import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { CATEGORIES } from '../constants';
import {
  computeSourceStats,
  DATA_WINDOW_DAYS,
  feedNamesFromItems,
  isStatusOk,
  sortSources,
  sourceStaleness,
  splitSourcesByType,
  STALE_AFTER_DAYS,
  type SourceStat,
  type Staleness,
} from '../feedSources';
import { S } from '../strings';
import type { FeedSource, ParsedItem, SourceType } from '../types';

type Props = {
  open: boolean;
  // Raw loaded data (not mute- or bookmark-filtered): the counts describe
  // ingestion health, not what the user currently sees. Official items only.
  items: readonly ParsedItem[];
  // Raw community items, counted against the "Yhteisö" section's sources.
  communityItems?: readonly ParsedItem[];
  // Feed list from Dataverse clarito_feedsource (incl. disabled feeds); empty
  // when the backend did not return it, which switches to the fallback view.
  sources: readonly FeedSource[];
  onClose: () => void;
  // Clock for the staleness rule; injectable for tests. Defaults to now.
  now?: Date;
};

const PANEL_WIDTH = 680;
const MOBILE_BREAKPOINT = 900;
const TITLE_ID = 'data-sources-title';
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const NO_ITEMS: readonly ParsedItem[] = [];

// Informational "Tietolähteet" panel opened from the header link: the RSS
// feeds listed in Dataverse clarito_feedsource in two sections, "Viralliset"
// and "Yhteisö" (independent bloggers, sourceType 'community'), each ordered by
// sortOrder, with the daily flow's last run time, status and new-item count
// per feed, plus per-feed item count and newest item date from the currently
// loaded 180-day window. Disabled feeds are hidden behind a per-section
// "Näytä myös pois käytöstä (n)" toggle and marked when shown. Enabled,
// already-fetched official feeds with no item newer than STALE_AFTER_DAYS (or
// none at all) get a warning note under the name; community feeds never do.
// Without `sources` it notes the failure and lists the feed names found in the
// loaded official rows. Same slide-in / bottom-sheet layout, backdrop and Esc
// handling as DetailPanel.
export function DataSourcesPanel({
  open,
  items,
  communityItems = NO_ITEMS,
  sources,
  onClose,
  now,
}: Props) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  // Note: the flow returns [] both when the table read failed and when the
  // read succeeded but clarito_feedsource has no rows; both show the
  // "Lähdetietoja ei saatu ladattua." note and the data-derived name list.
  const fallback = sources.length === 0;
  const { official, community } = useMemo(() => splitSourcesByType(sources), [sources]);
  const fallbackNames = useMemo(
    () => (fallback ? feedNamesFromItems(items) : []),
    [fallback, items],
  );
  const fallbackStats = useMemo(
    () => computeSourceStats(items, fallbackNames),
    [items, fallbackNames],
  );
  // One clock reading per render for every row's staleness check.
  const nowAt = now ?? new Date();

  // Esc closes; Tab / Shift+Tab cycle within the panel (aria-modal="true"
  // promises focus can't wander to the page behind the backdrop).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      const inside = dialogRef.current.contains(active);
      if (e.shiftKey && (active === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Move focus into the panel on open and hand it back to the trigger on close.
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => previous?.focus?.();
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.18)',
          zIndex: 40,
        }}
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        style={{
          position: 'fixed',
          background: 'var(--card-bg, #fff)',
          boxShadow: '-12px 0 32px rgba(0,0,0,0.18)',
          zIndex: 50,
          overflowY: 'auto',
          ...panelLayout(),
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            borderBottom: '1px solid var(--border, #E5E7EB)',
            position: 'sticky',
            top: 0,
            background: 'var(--card-bg, #fff)',
            zIndex: 1,
          }}
        >
          <h2
            id={TITLE_ID}
            style={{
              margin: 0,
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--text-muted, #6B7280)',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            {S.dataSources.title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={S.dataSources.close}
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              border: 'none',
              background: 'var(--surface-muted, rgba(148,163,184,0.15))',
              color: 'var(--text, #1F2937)',
              fontSize: 18,
              fontWeight: 700,
              lineHeight: 1,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 16 }}>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text, #1F2937)' }}>
            {S.dataSources.intro}
          </p>
          {fallback && (
            <p
              role="status"
              style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--text-muted, #6B7280)' }}
            >
              {S.dataSources.loadFailed}
            </p>
          )}
          {fallback ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>{S.dataSources.colSource}</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>{S.dataSources.colCount}</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>{S.dataSources.colNewest}</th>
                  </tr>
                </thead>
                <tbody>
                  {fallbackNames.map((name) => (
                    <tr key={name}>
                      <td style={tdStyle}>{name}</td>
                      <StatCells stat={fallbackStats.byFeed.get(name) ?? EMPTY_STAT} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <>
              <SourceSection
                type="official"
                title={S.dataSources.sectionOfficial}
                sources={official}
                items={items}
                now={nowAt}
              />
              <SourceSection
                type="community"
                title={S.dataSources.sectionCommunity}
                intro={S.dataSources.communityIntro}
                sources={community}
                items={communityItems}
                now={nowAt}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}

const EMPTY_STAT: SourceStat = { count: 0, newest: null };

// One section ("Viralliset" or "Yhteisö") of the panel: heading, optional
// intro, the disabled-rows toggle and the table. Disabled rows are hidden
// until the toggle is on; their items still count towards their own row, not
// "Muu". Community rows show the author in place of the category.
function SourceSection({
  type,
  title,
  intro,
  sources,
  items,
  now,
}: {
  type: SourceType;
  title: string;
  intro?: string;
  sources: readonly FeedSource[];
  items: readonly ParsedItem[];
  now: Date;
}) {
  const headingId = useId();
  const [showDisabled, setShowDisabled] = useState(false);
  const sorted = useMemo(() => sortSources(sources), [sources]);
  const stats = useMemo(
    () => computeSourceStats(items, sorted.map((s) => s.name)),
    [items, sorted],
  );
  const disabledCount = sorted.filter((s) => !s.enabled).length;
  const rows = showDisabled ? sorted : sorted.filter((s) => s.enabled);
  const community = type === 'community';
  const hasTable = rows.length > 0 || stats.other.count > 0;

  return (
    <section aria-labelledby={headingId} style={{ marginBottom: 18 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 6,
        }}
      >
        <h3
          id={headingId}
          style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text, #1F2937)' }}
        >
          {title}
        </h3>
        {disabledCount > 0 && (
          <button
            type="button"
            onClick={() => setShowDisabled((v) => !v)}
            aria-pressed={showDisabled}
            style={{
              padding: 0,
              border: 'none',
              background: 'none',
              color: 'var(--text-muted, #6B7280)',
              fontSize: 11,
              textDecoration: 'underline',
              textUnderlineOffset: 2,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {S.dataSources.showDisabled(disabledCount)}
          </button>
        )}
      </div>
      {intro && (
        <p style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--text-muted, #6B7280)' }}>
          {intro}
        </p>
      )}
      {sorted.length === 0 && stats.other.count === 0 && community && (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted, #6B7280)' }}>
          {S.dataSources.communityEmpty}
        </p>
      )}
      {hasTable && (
        <div style={{ overflowX: 'auto' }}>
          <table
            aria-labelledby={headingId}
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
          >
            <thead>
              <tr>
                <th style={thStyle}>{S.dataSources.colSource}</th>
                <th style={thStyle}>
                  {community ? S.dataSources.colAuthor : S.dataSources.colCategory}
                </th>
                <th style={{ ...thStyle, textAlign: 'right' }}>{S.dataSources.colCount}</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>{S.dataSources.colNewest}</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>{S.dataSources.colLastRun}</th>
                <th style={thStyle}>{S.dataSources.colStatus}</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>{S.dataSources.colNewItems}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((src, i) => {
                const stat = stats.byFeed.get(src.name) ?? EMPTY_STAT;
                const staleness = sourceStaleness(src, stat, now);
                return (
                  <tr
                    key={`${i}:${src.name}`}
                    style={src.enabled ? undefined : { color: 'var(--text-muted, #6B7280)' }}
                  >
                    <td style={tdStyle}>
                      {src.homeUrl ? (
                        <a href={src.homeUrl} target="_blank" rel="noopener noreferrer">
                          {src.name}
                        </a>
                      ) : (
                        src.name
                      )}
                      {!src.enabled && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted, #6B7280)' }}>
                          {S.dataSources.disabled}
                        </div>
                      )}
                      {staleness && <StaleNote staleness={staleness} />}
                    </td>
                    <td style={{ ...tdStyle, color: 'var(--text-muted, #6B7280)' }}>
                      {community
                        ? (src.author ?? S.dataSources.none)
                        : src.category
                          ? categoryLabel(src.category)
                          : S.dataSources.none}
                    </td>
                    <StatCells stat={stat} />
                    <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {formatRun(src.lastRun)}
                    </td>
                    <td style={tdStyle}>
                      <StatusBadge status={src.lastStatus} />
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {src.lastNewItems ?? S.dataSources.none}
                    </td>
                  </tr>
                );
              })}
              {stats.other.count > 0 && (
                <tr>
                  <td style={tdStyle}>
                    {S.dataSources.other}
                    <div style={{ fontSize: 11, color: 'var(--text-muted, #6B7280)' }}>
                      {stats.other.feeds.join(', ')}
                    </div>
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-muted, #6B7280)' }}>
                    {S.dataSources.none}
                  </td>
                  <StatCells stat={stats.other} />
                  <td style={tdStyle} />
                  <td style={tdStyle} />
                  <td style={tdStyle} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// Count + newest-date cells computed from the loaded 180-day window.
function StatCells({ stat }: { stat: SourceStat }) {
  return (
    <>
      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {stat.count}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
        {stat.count > 0 && stat.newest
          ? stat.newest.toLocaleDateString('fi-FI')
          : S.dataSources.none}
      </td>
    </>
  );
}

// Warning chip under a stale source's name, in the same amber as a non-OK
// status. The status itself stays visible (typically still "OK").
function StaleNote({ staleness }: { staleness: Staleness }) {
  return (
    <div style={{ marginTop: 2 }}>
      <span
        style={{
          display: 'inline-block',
          padding: '1px 6px',
          borderRadius: 4,
          background: '#FFF4CE',
          color: '#8A5300',
          fontSize: 11,
          fontWeight: 600,
        }}
      >
        {staleness === 'empty'
          ? S.dataSources.staleEmpty(DATA_WINDOW_DAYS)
          : S.dataSources.staleOld(STALE_AFTER_DAYS)}
      </span>
    </div>
  );
}

// OK in green; anything else as a warning chip with the flow's status text,
// truncated, full text in the tooltip.
function StatusBadge({ status }: { status: string | null }) {
  if (status === null) return <>{S.dataSources.none}</>;
  if (isStatusOk(status)) {
    return <span style={{ color: '#107C10', fontWeight: 600 }}>{status}</span>;
  }
  return (
    <span
      title={status}
      style={{
        display: 'inline-block',
        maxWidth: 160,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        verticalAlign: 'bottom',
        padding: '1px 6px',
        borderRadius: 4,
        background: '#FFF4CE',
        color: '#8A5300',
        fontWeight: 600,
      }}
    >
      {status}
    </span>
  );
}

function formatRun(lastRun: string | null): string {
  if (!lastRun) return S.dataSources.none;
  const d = new Date(lastRun);
  if (isNaN(d.getTime())) return S.dataSources.none;
  return d.toLocaleString('fi-FI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function categoryLabel(value: string): string {
  return CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--text-muted, #6B7280)',
  borderBottom: '1px solid var(--border, #E5E7EB)',
};

const tdStyle: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid var(--border, #E5E7EB)',
  verticalAlign: 'top',
};

function panelLayout(): React.CSSProperties {
  if (typeof window === 'undefined' || window.innerWidth > MOBILE_BREAKPOINT) {
    return { top: 0, right: 0, bottom: 0, width: PANEL_WIDTH, maxWidth: '100vw' };
  }
  return {
    left: 0,
    right: 0,
    bottom: 0,
    maxHeight: '85vh',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  };
}
